import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import { Box, Chip, Grid, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { translateText } from '../../../language'
import { useThemeMode } from '../../../theme'
import { Button as PillButton, ReachingIcon } from '../widgets'
import { VotingCountdown } from '../voting/Countdown'

const makeStyles = (palette) => ({
  paper: { elevation: 2, square: true },
  grid: { px: 2, pt: 1, pb: 1, alignItems: 'center', maxWidth: '700px', margin: '0 auto' },
  iconCol: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  contentCol: { display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 },
  title: { color: palette.headerColor, lineHeight: 1.2 },
  filterRow: (divider) => ({ pt: 1, borderTop: divider, display: 'flex', justifyContent: 'center' }),
  filterLabel: { opacity: 0.6, mb: 0.5 },
  remainingSummary: { pt: 0.75 },
  remainingLabel: { opacity: 0.6, display: 'block', mb: 0.5 },
  filterToggle: (headerColor) => ({
    '& .MuiToggleButtonGroup-grouped': {
      borderColor: `${headerColor} !important`
    }
  }),
  filterToggleBtn: (headerColor, paperColor) => ({
    color: headerColor,
    borderColor: headerColor,
    fontSize: '0.65rem',
    py: 0.25,
    px: 0.75,
    lineHeight: 1.4,
    minWidth: 0,
    whiteSpace: 'nowrap',
    '&.Mui-selected': {
      backgroundColor: headerColor,
      color: paperColor,
      '&:hover': { backgroundColor: headerColor }
    },
    '&:hover': { backgroundColor: `${headerColor}18` }
  }),
  backLink: { color: palette.headerColor, opacity: 0.7, textDecoration: 'none', display: 'block', lineHeight: 1, fontSize: '0.75rem' },
  remainingChip: (remaining, headerColor, textColor) => ({
    width: '100%',
    backgroundColor: remaining > 0 ? `${headerColor}22` : 'transparent',
    color: remaining > 0 ? headerColor : textColor,
    border: `1px solid ${remaining > 0 ? headerColor : 'rgba(0,0,0,0.15)'}`,
    fontSize: '0.7rem',
    height: '20px',
    opacity: remaining === 0 ? 0.5 : 1,
    '& .MuiChip-label': { px: 0.75, display: 'flex', overflow: 'hidden', width: '100%', gap: '0.1em' }
  }),
  chipLabelText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 0', minWidth: 0 },
  chipLabelCount: { flexShrink: 0, whiteSpace: 'nowrap' }
})

const VotesSummary = ({ campaigns, styles, palette, t }) => {
  if (campaigns.length === 0) return null
  return (
    <Grid item xs={12} sx={styles.remainingSummary}>
      <Typography variant="caption" sx={styles.remainingLabel}>
        {t('snacks-votes-remaining')}:
      </Typography>
      <Grid container columns={3} spacing={0.75}>
        {campaigns.map(({ name, label, remaining, max }) => (
          <Grid item xs={1} key={name}>
            <Chip
              label={
                <>
                  <Box component="span" sx={styles.chipLabelText}>
                    {label}
                  </Box>
                  <Box component="span" sx={styles.chipLabelCount}>
                    : {remaining}/{max}
                  </Box>
                </>
              }
              size="small"
              sx={styles.remainingChip(remaining, palette.headerColor, palette.textColor)}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}

export const SnacksPageHeader = ({
  showRegister,
  onRegister,
  registerLabel = 'snacks-register-table',
  showEdit,
  onEdit,
  showScanQR,
  onScanQR,
  hideVotesSummary,
  votesFilter,
  onVotesFilterChange,
  campaignFilter,
  campaigns,
  onCampaignFilterChange,
  backTo
}) => {
  const { t, i18n } = useTranslation()
  const { palette } = useThemeMode()
  const snacksCampaigns = useSelector((state) => state.voting?.snacks_exchange?.campaigns ?? [])
  const deadline = useSelector((state) => state.voting?.snacks_exchange?.deadline)
  const deadlineMs = useSelector((state) => state.voting?.snacks_exchange?.deadlineMs)
  const votingStart = useSelector((state) => state.voting?.snacks_exchange?.votingStart)
  const votingStartMs = useSelector((state) => state.voting?.snacks_exchange?.votingStartMs)
  const votingNotStarted = votingStart != null && votingStart > 0

  const showVotesFilter = votesFilter !== undefined && onVotesFilterChange !== undefined
  const showCampaignFilter = campaignFilter !== undefined && onCampaignFilterChange !== undefined && Array.isArray(campaigns)
  const divider = `1px solid ${palette.paperColorDivider ?? 'rgba(0,0,0,0.08)'}`

  const styles = makeStyles(palette)

  const remainingByCampaign = snacksCampaigns.map((campaign) => {
    const used = Math.min(
      campaign.votes.reduce((acc, v) => acc + v.value, 0),
      campaign.campaign_max
    )
    return {
      name: campaign.name,
      label: translateText(campaign.titles, i18n),
      remaining: campaign.campaign_max - used,
      max: campaign.campaign_max
    }
  })

  return (
    <Paper elevation={2} square>
      <Grid container sx={styles.grid}>
        <Grid item xs={3} sx={styles.iconCol}>
          <ReachingIcon sx={{ fontSize: 48, color: palette.headerColor }} />
        </Grid>
        <Grid item xs={9} sx={styles.contentCol}>
          <Box sx={styles.titleRow}>
            <Typography variant="subtitle1" fontWeight={700} sx={styles.title}>
              {t('snacks-title')}
            </Typography>
            {showVotesFilter && (
              <ToggleButtonGroup
                value={votesFilter}
                exclusive
                onChange={(_, v) => v && onVotesFilterChange(v)}
                size="small"
                sx={styles.filterToggle(palette.headerColor)}
              >
                {[
                  { value: 'all', label: t('snacks-filter-all') },
                  { value: 'voted', label: t('snacks-filter-my-votes') }
                ].map(({ value, label }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={styles.filterToggleBtn(palette.headerColor, palette.paperColor)}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>
          {(showRegister || showEdit || showScanQR) && (
            <Box sx={{ mt: 1.5, mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              {showRegister && (
                <PillButton
                  text={registerLabel}
                  variant="filled"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={onRegister}
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {showEdit && (
                <PillButton
                  text="snacks-edit-my-table"
                  variant="filled"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={onEdit}
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {showScanQR && (
                <PillButton
                  text="snacks-qr-scan-button"
                  variant="filled"
                  size="small"
                  startIcon={<QrCodeScannerIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={onScanQR}
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        </Grid>

        {!showCampaignFilter && !hideVotesSummary && (
          <VotesSummary campaigns={remainingByCampaign} styles={styles} palette={palette} t={t} />
        )}

        {showCampaignFilter && (
          <Grid item xs={12} sx={styles.filterRow(divider)}>
            <ToggleButtonGroup
              value={campaignFilter}
              exclusive
              onChange={(_, v) => v && onCampaignFilterChange(v)}
              size="small"
              sx={{ ...styles.filterToggle(palette.headerColor), flexWrap: 'wrap' }}
            >
              {[{ name: 'all', label: t('snacks-results-filter-all') }, ...campaigns].map(({ name, label }) => (
                <ToggleButton key={name} value={name} sx={styles.filterToggleBtn(palette.headerColor, palette.paperColor)}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
        )}

        {(deadline !== undefined || votingNotStarted) && (
          <Grid item xs={12}>
            <VotingCountdown deadlineMs={deadlineMs} votingStartMs={votingStartMs} votingNotStarted={votingNotStarted} />
          </Grid>
        )}

        {backTo && (
          <Grid item xs={12} sx={{ pt: 0.75, pb: 0.25 }}>
            <Typography component={Link} to={backTo} variant="caption" sx={styles.backLink}>
              {t('snacks-back-to-all')}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}
